/* eslint-disable no-console */
/**
 * Обёртка над localStorage с откатом на куки.
 * Нужна для окружений, где localStorage недоступен (например, Android WebView)
 */
import { getTracerModules } from '@apptracer/sdk/lite';

import { packageName } from '../../../config';

const COOKIE_EXPIRE_DAYS = 3;

/** Проверяет доступность localStorage */
const isLocalStorageAvailable = (): boolean => {
  const tracerError = getTracerModules(packageName).error;

  try {
    if (typeof window.localStorage === 'undefined' || window.localStorage === null) {
      return false;
    }

    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (_e) {
    tracerError?.error('LocalStorage недоступен!');

    return false;
  }
};

const setCookie = (name: string, value: string, days?: number): void => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const eraseCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

class StorageManager implements StorageInterface {
  private storageAvailable: boolean;
  private tracerError = getTracerModules(packageName).error;

  constructor() {
    try {
      this.storageAvailable = isLocalStorageAvailable();
    } catch (_e) {
      this.storageAvailable = false;
    }

    if (!this.storageAvailable) {
      console.warn('localStorage is not available. Using cookies as fallback.');
    }
  }

  getItem(key: string): string | null {
    if (this.storageAvailable) {
      try {
        // Повторная проверка: localStorage мог стать недоступен после инициализации
        if (typeof window.localStorage === 'undefined' || window.localStorage === null) {
          return getCookie(key);
        }
        return window.localStorage.getItem(key);
      } catch (_e) {
        this.tracerError?.error('Ошибка при попытке доступа к LocalStorage!');
        return getCookie(key);
      }
    }

    return getCookie(key);
  }

  setItem(key: string, value: string): void {
    if (this.storageAvailable) {
      try {
        // Повторная проверка: localStorage мог стать недоступен после инициализации
        if (typeof window.localStorage === 'undefined' || window.localStorage === null) {
          setCookie(key, value, COOKIE_EXPIRE_DAYS);
          return;
        }
        window.localStorage.setItem(key, value);
      } catch (_e) {
        this.tracerError?.error('Ошибка при попытке записи в LocalStorage!');
        // Откат на куки
        setCookie(key, value, COOKIE_EXPIRE_DAYS);
      }
    } else {
      setCookie(key, value, COOKIE_EXPIRE_DAYS);
    }
  }

  removeItem(key: string): void {
    if (this.storageAvailable) {
      try {
        // Повторная проверка: localStorage мог стать недоступен после инициализации
        if (typeof window.localStorage === 'undefined' || window.localStorage === null) {
          eraseCookie(key);
          return;
        }
        window.localStorage.removeItem(key);
      } catch (_e) {
        this.tracerError?.error('Ошибка при попытке удаления из LocalStorage!');
        eraseCookie(key);
      }
    } else {
      eraseCookie(key);
    }
  }
}

export const getBooleanFromStorage = (key: string): boolean | undefined => {
  const tracerError = getTracerModules(packageName).error;
  try {
    const value = storageManager.getItem(key);
    return value === 'true' ? true : value === 'false' ? false : undefined;
  } catch (_) {
    tracerError?.error('Ошибка при попытке чтения из LocalStorage!', {
      keys: {
        key,
      },
    });
    return undefined;
  }
};

export const setBooleanToStorage = (key: string, value: boolean): void => {
  const tracerError = getTracerModules(packageName).error;

  try {
    storageManager.setItem(key, String(value));
  } catch (_) {
    tracerError?.error('Ошибка при попытке записи в LocalStorage!', {
      keys: {
        key,
      },
    });
  }
};

export const storageManager = new StorageManager();
